import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MissionMomentum, QuestCheckpoint } from "@/components/Retention";
import { firstName } from "@/lib/name";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { SaveNote } from "@/components/SaveNote";
import { showWinCard } from "@/components/WinCard";
import { Loader2, Clock, Zap, Check, Lock, Trophy, ChevronRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, apiError } from "@/lib/api";
import { QuestCertificate } from "@/components/QuestCertificate";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function MissionTab() {
  const [m, setM] = useState(null);
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();

  const load = () => api.get("/missions/today").then(({ data }) => { setM(data); setDone(data.completed_today); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const complete = async () => {
    if (response.trim().length < 5) { toast.error("Write a short response to complete the mission."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/missions/complete", { mission_key: m.key, response });
      confetti({ particleCount: 120, spread: 75, origin: { y: 0.6 }, colors: ["#4F46E5", "#F59E0B", "#10B981"] });
      toast.success(`Mission complete! +${data.xp_earned} XP · Readiness ${data.readiness}%`);
      data.new_achievements?.forEach((a) => toast.success(`🏆 ${a}`));
      if ([3, 7, 14, 21, 30, 50, 100].includes(data.streak)) setTimeout(() => showWinCard({ kind: "streak", days: data.streak }), 900);
      setDone(true);
      await Promise.all([refreshUser(), load()]);
    } catch (e) {
      if (e?.response?.status === 402) navigate("/paywall");
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  if (!m) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  if (m.locked) return (
    <div className="rounded-3xl border-2 border-dashed border-indigo-200 bg-indigo-50/50 p-8 text-center">
      <Lock className="mx-auto h-8 w-8 text-indigo-600" />
      <p className="mt-3 font-display text-lg font-bold">You've used your {m.weekly_limit} free missions this week</p>
      <p className="mt-1 text-sm text-muted-foreground">Upgrade to NxT Plus for a fresh mission every single day.</p>
      <button onClick={() => navigate("/paywall")} data-testid="mission-upgrade-btn"
        className="mt-4 rounded-full bg-slate-900 px-6 py-3 font-bold text-white hover:bg-indigo-600">Unlock daily missions</button>
    </div>
  );

  return (
    <div className="rounded-3xl border border-border bg-card p-6" data-testid="mission-tab">
      <p className="mb-3 text-xs font-bold uppercase tracking-widest text-indigo-700" data-testid="mission-personal-name">{firstName(user)}'s Daily Mission</p>
      <MissionMomentum status={m.streak_status} readiness={m.readiness} missionTitle={m.title} />
      <div className="mt-4 flex items-center justify-between">
        <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-bold text-indigo-700">{m.category}</span>
        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">+{m.xp} XP</span>
      </div>
      <h2 className="mt-3 font-display text-2xl font-extrabold">{m.title}</h2>
      <p className="mt-2 text-muted-foreground">{m.description}</p>
      <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> {m.time_min} min</div>
      <div className="mt-3 rounded-2xl bg-indigo-50 p-3 text-sm text-indigo-800"><span className="font-bold">Why it matters: </span>{m.why}</div>

      {done ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-2xl bg-emerald-50 py-6 text-emerald-700">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white"><Check className="h-6 w-6" /></div>
          <p className="font-display text-lg font-bold">Mission complete ✓</p>
          <p className="text-sm">Come back tomorrow for your next one.</p>
          {(m.response || response) && <SaveNote testId="mission-save-notebook" title={`Reflection · ${m.title}`} content={`${m.title}\n\n${m.response || response}`} category="reflection" source="mission" sourceRef={`${m.key}-${m.date}`} />}
        </div>
      ) : (
        <>
          <textarea data-testid="mission-response-input" value={response} onChange={(e) => setResponse(e.target.value)}
            rows={4} placeholder="Do the mission, then write your answer / notes here…"
            className="mt-4 w-full rounded-2xl border border-input bg-background px-4 py-3 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" />
          <button onClick={complete} disabled={busy} data-testid="complete-mission-btn"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 py-3.5 font-bold text-white transition-colors hover:bg-indigo-600 disabled:opacity-60">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Zap className="h-5 w-5" /> Complete Mission</>}
          </button>
        </>
      )}
      {m.weekly_limit && !done && <p className="mt-3 text-center text-xs text-muted-foreground">{m.weekly_used}/{m.weekly_limit} free missions used this week</p>}
    </div>
  );
}

function QuestsTab() {
  const [quests, setQuests] = useState([]);
  const [busy, setBusy] = useState("");
  const [cert, setCert] = useState(null);
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();

  const load = () => api.get("/quests").then(({ data }) => setQuests(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (q) => {
    setBusy(q.key);
    try {
      if (!q.started) { await api.post("/quests/start", { quest_key: q.key }); toast.success("Quest started!"); }
      else if (q.completed_levels < q.total_levels) {
        const { data } = await api.post("/quests/complete-level", { quest_key: q.key });
        confetti({ particleCount: 80, spread: 60, origin: { y: 0.6 } });
        toast.success(data.quest_completed ? "🎉 Quest complete! Your certificate is ready." : `Level up! +80 XP`);
        if (data.midpoint_just_reached) toast.success("Midpoint reached — your first proof is taking shape.");
        if (data.quest_completed) { confetti({ particleCount: 200, spread: 100, origin: { y: 0.4 } }); setCert(q.key); }
      }
      await load(); await refreshUser();
    } catch (e) {
      if (e?.response?.status === 402) navigate("/paywall");
      else toast.error(apiError(e));
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="quests-tab">
      {cert && <QuestCertificate questKey={cert} onClose={() => setCert(null)} />}
      {quests.map((q) => {
        const pct = (q.completed_levels / q.total_levels) * 100;
        const complete = q.completed_levels >= q.total_levels;
        return (
          <div key={q.key} className="rounded-3xl border border-border bg-card p-5" data-testid={`quest-${q.key}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /><h3 className="font-display text-lg font-bold">{q.title}</h3></div>
                <p className="mt-1 text-sm text-muted-foreground">{q.description}</p>
              </div>
              <span className="shrink-0 rounded-full bg-secondary px-2.5 py-1 text-xs font-bold">{q.duration_days}d</span>
            </div>
            {q.started && (
              <div className="mt-4">
                <QuestCheckpoint questKey={q.key} checkpoint={q.checkpoint} done={q.completed_levels} total={q.total_levels} />
                <p className="mt-2 text-xs text-muted-foreground">Next: <span className="font-semibold text-foreground">{complete ? "Completed 🎉" : q.levels[q.completed_levels]}</span></p>
              </div>
            )}
            {complete ? (
              <button onClick={() => setCert(q.key)} data-testid={`quest-certificate-${q.key}`}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 py-3 font-bold text-slate-900 transition-colors hover:bg-amber-300">
                <Trophy className="h-4 w-4" /> View certificate
              </button>
            ) : (
              <button onClick={() => act(q)} disabled={busy === q.key} data-testid={`quest-action-${q.key}`}
                className={`mt-4 flex w-full items-center justify-center gap-2 rounded-full py-3 font-bold transition-colors disabled:opacity-60 ${q.started ? "bg-slate-900 text-white hover:bg-indigo-600" : "bg-indigo-600 text-white hover:bg-indigo-700"}`}>
                {busy === q.key ? <Loader2 className="h-5 w-5 animate-spin" /> : q.started ? <>Complete next level <ChevronRight className="h-4 w-4" /></> : "Start Quest"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Missions() {
  const [params] = useSearchParams();
  return (
    <div className="space-y-5">
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Missions & Quests</h1>
      <Tabs defaultValue={params.get("tab") === "quests" ? "quests" : "mission"}>
        <TabsList className="grid w-full grid-cols-2" data-testid="missions-tabs">
          <TabsTrigger value="mission" data-testid="tab-mission">Daily Mission</TabsTrigger>
          <TabsTrigger value="quests" data-testid="tab-quests">Career Quests</TabsTrigger>
        </TabsList>
        <TabsContent value="mission" className="mt-4"><MissionTab /></TabsContent>
        <TabsContent value="quests" className="mt-4"><QuestsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
